import { HolidayWindow } from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { PartnerBusinessState, PartnerBusinessStatus } from './partner-business-status.types';

dayjs.extend(utc);
dayjs.extend(timezone);

type DisplayTime = (input: string | Date | Dayjs) => string;

const WEEK_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEK_DAY_NAME_FORMAT = 'dddd';
const SHORT_DATE_FORMAT = 'ddd, MMM D';
const TIME_ZONE_OFFSET_FORMAT = '[GMT]Z';
const CLOSED_LABEL = 'Closed';

export interface WeeklyHoursRow {
  weekDay: number;
  dayLabel: string;
  windows: string[];
  isToday: boolean;
}

export interface ClosureNote {
  name: string;
  closedRange: string;
  reopens: string | null;
}

export function buildStatusLabel(status: PartnerBusinessStatus): string {
  if (status.state !== PartnerBusinessState.Holiday) {
    return status.state === PartnerBusinessState.Open ? 'Open' : CLOSED_LABEL;
  }

  const name = status.activeHoliday?.holidayName;

  return name ? `${CLOSED_LABEL} · ${name}` : CLOSED_LABEL;
}

export function buildHeadsUpLabel(status: PartnerBusinessStatus): string | null {
  if (
    status.state !== PartnerBusinessState.Open ||
    !status.upcomingHoliday ||
    !status.upcomingHolidayAt
  ) {
    return null;
  }

  const startsOn = toPartnerTime(status.upcomingHolidayAt, status);
  const dayLabel = startsOn.isSame(toPartnerTime(status.currentTime, status), 'day')
    ? 'Today'
    : startsOn.format(WEEK_DAY_NAME_FORMAT);

  return `Holiday ${dayLabel}`;
}

export function buildBoundaryLine(
  status: PartnerBusinessStatus,
  displayTime: DisplayTime,
): string | null {
  if (status.state === PartnerBusinessState.Holiday) {
    return null;
  }

  if (status.state === PartnerBusinessState.Open) {
    return status.openUntil
      ? `Open now · closes ${displayTime(status.openUntil)}`
      : 'Open now';
  }

  if (!status.opensAt) {
    return CLOSED_LABEL;
  }

  const dayLabel = buildRelativeDayLabel(status.opensAt, status);

  return `${CLOSED_LABEL} · opens ${displayTime(status.opensAt)} ${dayLabel}`;
}

/**
 * The note describes the closure the agent needs to know about: the one in force right now,
 * or otherwise the one waiting on the next day the partner would have been open.
 */
export function buildClosureNote(
  status: PartnerBusinessStatus,
  displayTime: DisplayTime,
): ClosureNote | null {
  const holiday = status.activeHoliday ?? status.upcomingHoliday;

  if (!holiday) {
    return null;
  }

  const reopensAt = status.activeHoliday ? status.opensAt : status.upcomingReopensAt;

  return {
    name: holiday.holidayName || CLOSED_LABEL,
    closedRange: buildClosedRange(holiday, status, displayTime),
    reopens: reopensAt ? buildReopensLine(reopensAt, status, displayTime) : null,
  };
}

export function buildWeeklyHoursRows(
  status: PartnerBusinessStatus,
  timeFormat: string,
): WeeklyHoursRow[] {
  const todayWeekDay = toPartnerTime(status.currentTime, status).day();

  return WEEK_DAY_ORDER.map((weekDay) => {
    const windows = status.weeklyHours
      .filter((window) => window.weekDay === weekDay)
      .sort((left, right) => left.timeFrom.localeCompare(right.timeFrom))
      .map(
        (window) =>
          `${formatWallClock(window.timeFrom, timeFormat)} – ${formatWallClock(window.timeTo, timeFormat)}`,
      );

    return {
      weekDay,
      dayLabel: dayjs().day(weekDay).format(WEEK_DAY_NAME_FORMAT),
      windows: windows.length ? windows : [CLOSED_LABEL],
      isToday: weekDay === todayWeekDay,
    };
  });
}

export function buildPartnerTimeFooter(status: PartnerBusinessStatus): string {
  if (!status.timeZone) {
    return 'Partner time';
  }

  return `Partner time · ${toPartnerTime(status.currentTime, status).format(TIME_ZONE_OFFSET_FORMAT)}`;
}

function buildClosedRange(
  holiday: HolidayWindow,
  status: PartnerBusinessStatus,
  displayTime: DisplayTime,
): string {
  const start = toPartnerTime(holiday.holidayStart, status);
  const end = toPartnerTime(holiday.holidayEnd, status);
  // The window is half-open, so the last covered day is the one just before it ends.
  const lastDay = end.subtract(1, 'millisecond');
  const isSingleDay = start.isSame(lastDay, 'day');

  if (isFullDayClosure(start, end)) {
    return isSingleDay
      ? `${CLOSED_LABEL} ${start.format(SHORT_DATE_FORMAT)}`
      : `${CLOSED_LABEL} ${start.format(SHORT_DATE_FORMAT)} – ${lastDay.format(SHORT_DATE_FORMAT)}`;
  }

  if (isSingleDay) {
    const dayLabel = start.isSame(toPartnerTime(status.currentTime, status), 'day')
      ? 'today'
      : start.format(SHORT_DATE_FORMAT);

    return `${CLOSED_LABEL} ${dayLabel}, ${displayTime(start)} – ${displayTime(end)}`;
  }

  return `${CLOSED_LABEL} ${start.format(SHORT_DATE_FORMAT)} ${displayTime(start)} – ${lastDay.format(SHORT_DATE_FORMAT)} ${displayTime(end)}`;
}

function buildReopensLine(
  reopensAt: Date,
  status: PartnerBusinessStatus,
  displayTime: DisplayTime,
): string {
  const reopensOn = toPartnerTime(reopensAt, status);
  const isToday = reopensOn.isSame(toPartnerTime(status.currentTime, status), 'day');

  return isToday
    ? `Reopens ${displayTime(reopensAt)} today`
    : `Reopens ${reopensOn.format(WEEK_DAY_NAME_FORMAT)} ${displayTime(reopensAt)}`;
}

function buildRelativeDayLabel(target: Date, status: PartnerBusinessStatus): string {
  const targetDay = toPartnerTime(target, status).startOf('day');
  const today = toPartnerTime(status.currentTime, status).startOf('day');
  const daysAhead = targetDay.diff(today, 'day');

  if (daysAhead <= 0) return 'today';
  if (daysAhead === 1) return 'tomorrow';

  return targetDay.format(WEEK_DAY_NAME_FORMAT);
}

function isFullDayClosure(start: Dayjs, end: Dayjs): boolean {
  const startsAtDayStart = start.isSame(start.startOf('day'));
  const lastMoment = end.subtract(1, 'millisecond');

  return startsAtDayStart && lastMoment.endOf('day').diff(end, 'minute') < 1;
}

function formatWallClock(time: string, timeFormat: string): string {
  const [hour, minute] = time.split(':').map(Number);

  return dayjs()
    .hour(hour || 0)
    .minute(minute || 0)
    .format(timeFormat);
}

function toPartnerTime(value: string | Date, status: PartnerBusinessStatus): Dayjs {
  return status.timeZone ? dayjs(value).tz(status.timeZone) : dayjs(value);
}
